import {
	type Message,
	type MessageType,
	type NavigationTypeType,
	type ParsedQpStack,
	type SDK_Colors,
	type SDK_Filter,
	type SDK_Fonts,
	type SDK_ViewStack,
	strings,
} from "@quickpoint.me/shared";
import { useRecoilCallback } from "recoil";
import { FilterNames, InjectedFilter } from "../state/filter";
import { RawCode, StackCfKey } from "../state/stack";
import {
	EditMode,
	HostId,
	IsInPresentationMode,
	IsPreviewGenerator,
	ReadMode,
} from "../state/ui";

import { useEffect, useRef } from "react";
import { NavigationType } from "../state/navigation";
import { LastRemotePageAtom, PageIndex } from "../state/page";

const toAuthorBC = (() => {
	try {
		const bc = new BroadcastChannel(strings.broadcastChannel.author);
		return bc;
	} catch {
		return false;
	}
})();

export function postMessageToAuthor({
	type,
	action,
	payload,
	hostId,
}: Message): void {
	if (toAuthorBC) {
		const message: Message = {
			hostId,
			type,
			action,
			payload: payload ? payload : undefined,
		};
		toAuthorBC.postMessage(message);
	}
}

export function sendInterpolatedColorsToAuthor(
	type: Message["type"],
	colors: SDK_Colors,
) {
	postMessageToAuthor({
		hostId: "all",
		type,
		action: strings.messages.setFilterColors,
		payload: colors,
	});
}

export function sendLoadedFontsToAuthor(
	type: Message["type"],
	fonts: SDK_Fonts,
) {
	postMessageToAuthor({
		hostId: strings.broadcastChannel.allAuthors,
		type,
		action: strings.messages.setFilterFonts,
		payload: fonts,
	});
}

export function sendParsedStackToAuthor(
	type: Message["type"],
	parsedStack: ParsedQpStack,
) {
	postMessageToAuthor({
		hostId: strings.broadcastChannel.allAuthors,
		type,
		action: strings.messages.parsedStack,
		// @ts-ignore
		payload: parsedStack,
	});
}

export function sendStoryNamesToAuthor(
	type: Message["type"],
	storyNames: string[],
) {
	postMessageToAuthor({
		hostId: strings.broadcastChannel.allAuthors,
		type,
		action: strings.messages.setStoryNames,
		payload: storyNames,
	});
}

export function sendNavigationTypeToAuthor(
	type: Message["type"],
	navigationType: NavigationTypeType,
) {
	postMessageToAuthor({
		hostId: strings.broadcastChannel.allAuthors,
		type,
		action: strings.messages.setNavigationType,
		payload: navigationType,
	});
}

export function sendElementClickToAuthor(
	type: Message["type"],
	payload: { lineNumber: number; pageIndex: number; elementType: string; content: string },
) {
	postMessageToAuthor({
		hostId: strings.broadcastChannel.allAuthors,
		type,
		action: strings.messages.elementClick,
		payload,
	});
}

/** Pop-out page turn → author follows (the page tie is two-way) */
export function sendGoToPageToAuthor(type: Message["type"], pageIndex: number) {
	postMessageToAuthor({
		hostId: strings.broadcastChannel.allAuthors,
		type,
		action: strings.messages.goToPage,
		payload: pageIndex,
	});
}

export function requestFilterNamesFromAuthor(type: Message["type"]) {
	postMessageToAuthor({
		hostId: strings.broadcastChannel.allAuthors,
		type,
		action: strings.messages.requestFilterNames,
	});
}

export function sendFiltersAreLoadedAlertToAuthor(
	type: Message["type"],
	payload?: string,
) {
	postMessageToAuthor({
		hostId: strings.broadcastChannel.allAuthors,
		type,
		action: strings.messages.filtersAreLoaded,
		payload,
	});
}

function MessageHandler() {
	// if the browser doesn't support Broadcast Channels disable
	// the ability of the viewer to receive messages from the author.

	const originalStackRef = useRef<SDK_ViewStack | null>(null);

	const handlePostMessage = useRecoilCallback(
		({ snapshot, set }) =>
			async (e: MessageEvent) => {
				const data: Message = e.data;
				const hostId = await snapshot.getPromise(HostId);
				const isPreviewer = await snapshot.getPromise(IsPreviewGenerator);
				const navType = await snapshot.getPromise(NavigationType);
				const stackPath = await snapshot.getPromise(StackCfKey);

				if (data.hostId !== hostId) return;

				if (data.action && data.action === strings.messages.edit) {
					if (typeof data.payload !== "number") {
						set(EditMode, true);
						const stack = data.payload as SDK_ViewStack;
						set(RawCode(stackPath), stack);
					}
				}

				if (
					data.action &&
					data.action === strings.messages.exitPresentationMode
				) {
					set(IsInPresentationMode, false);
					if (originalStackRef.current) {
						const targetStackPath = strings.utility.inAuthor;
						set(RawCode(targetStackPath), originalStackRef.current);
						originalStackRef.current = null;
					}
				}

				// inject  a filter
				if (data.action && data.action === strings.messages.injectFilter) {
					if (data.payload && typeof data.payload !== "number") {
						const filter = data.payload as SDK_Filter;
						set(InjectedFilter, filter);
					}
				}

				if (data.action && data.action === strings.messages.preview) {
					set(EditMode, false);
				}

				if (
					data.action &&
					data.action === strings.messages.presentationMode &&
					!isPreviewer
				) {
					if (data.payload && typeof data.payload !== "number") {
						const stack = data.payload as SDK_ViewStack;
						originalStackRef.current = stack;

						if (!stack.publish) {
							set(EditMode, false);
							set(IsInPresentationMode, true);
							return;
						}

						const { default: QRCodeStyling } = await import("styled-qr-code");
						const qrCode = new QRCodeStyling({
							width: 400,
							height: 400,
							data: `https://quickpoint.me/${stack.stackCfKey}`,
							margin: 10,
							// image: "https://quickpoint.me/QPLogo.svg",
							dotsOptions: {
								color: "#000000",
								type: "square",
							},
							imageOptions: {
								crossOrigin: "anonymous",
								margin: 10,
							},
							backgroundOptions: {
								color: "#ffffff",
							},
						});
						try {
							const buffer = await qrCode.getRawData("png");

							if (buffer) {
								// Wrap FileReader in a Promise to await it
								const base64data = await new Promise<string>(
									(resolve, reject) => {
										const reader = new FileReader();
										reader.readAsDataURL(buffer as Blob);
										reader.onloadend = () => {
											if (typeof reader.result === "string") {
												resolve(reader.result);
											} else {
												reject(new Error("Failed to convert blob to base64"));
											}
										};
										reader.onerror = (error) => {
											console.error("FileReader error:", error);
											reject(error);
										};
									},
								);

								const imageString = `\nimage: ${base64data}\n===\n`;
								const newCode = imageString + (stack.code || "");
								const newStack = {
									...stack,
									code: newCode,
								} as SDK_ViewStack;

								set(EditMode, false);
								set(IsInPresentationMode, true);

								// FIX: When in presentation mode, the app forces the StackCfKey to be 'inAuthor'.
								// We must update that specific RawCode atom, regardless of what the current stackPath is.
								const targetStackPath = strings.utility.inAuthor;
								set(RawCode(targetStackPath), newStack);
							} else {
								console.error("QR Code buffer is empty");
							}
						} catch (err) {
							console.error("Error in presentation mode flow:", err);
						}
					}
				}

				if (data.action && data.action === strings.messages.unPreview) {
					set(EditMode, true);
				}

				// set readMode
				if (data.action && data.action === strings.messages.enterReadMode) {
					set(ReadMode, true);
				}

				// set readMode
				if (data.action && data.action === strings.messages.exitReadMode) {
					set(ReadMode, false);
				}

				if (data.action && data.action === strings.messages.setFilterNames) {
					if (Array.isArray(data.payload)) {
						const f = data.payload as string[];
						set(FilterNames, f);
					}
				}

				if (
					data.type === "preview" &&
					isPreviewer &&
					data.action &&
					data.action === strings.messages.goToPage
				) {
					set(EditMode, true);
					set(PageIndex, (prev: number) => {
						// pageIndex is base 1.
						if (data.payload && typeof data.payload === "number") {
							// //console.log("V: message received: ", data.payload);
							return data.payload;
						}
						// if the payload is incompatible stay on the same page
						return prev;
					});
				}

				if (
					data.type === "QP" &&
					!isPreviewer &&
					data.action &&
					data.action === strings.messages.goToPage
				) {
					if (navType !== "tap") {
						const scrollTriggerDiv = document.getElementById(
							`scroll-trigger-page-${data.payload}`,
						);
						scrollTriggerDiv?.scrollIntoView({
							behavior: "instant",
							block: "start",
						});
					}
					if (data.payload && typeof data.payload === "number") {
						// remember the remote origin so the pop-out doesn't echo it back
						set(LastRemotePageAtom, data.payload);
					}
					set(PageIndex, (prev: number) => {
						// pageIndex is base 1.
						if (data.payload && typeof data.payload === "number") {
							return data.payload;
						}
						// if the payload is incompatible stay on the same page
						return prev;
					});
				}
			},
		[],
	);

	useEffect(() => {
		const bc = new BroadcastChannel(strings.broadcastChannel.viewer);
		bc.onmessage = (e: MessageEvent<MessageType>) => {
			handlePostMessage(e);
		};

		return () => {
			bc.close();
		};
	}, [handlePostMessage]);

	try {
		BroadcastChannel;
	} catch {
		console.error("No BroadcastChannel");
		return <></>;
	}

	return <></>;
}

export default MessageHandler;
